import { Layers, Rocket, Zap } from 'lucide-react';
import { useState } from 'react';
import { cn } from '~/lib/utils';

type Preset = 'lightweight' | 'standard' | 'enterprise';

interface SpecFeaturePreview {
  feature: { name: string; slug: string };
  definition: { dependencies: string[] };
  isRequired: boolean;
  defaultPreset: string | null | undefined;
}

interface Props {
  value: string;
  onChange: (preset: Preset) => void;
  specFeatures?: SpecFeaturePreview[];
}

const PRESETS: {
  id: Preset;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  {
    id: 'lightweight',
    label: 'Lightweight',
    description: 'Minimal footprint, fast to ship',
    icon: Zap,
  },
  {
    id: 'standard',
    label: 'Standard',
    description: 'Balanced for most products',
    icon: Layers,
  },
  {
    id: 'enterprise',
    label: 'Enterprise',
    description: 'Full feature set, maximum scale',
    icon: Rocket,
  },
];

function isEnabledInPreset(sf: SpecFeaturePreview, preset: Preset): boolean {
  return (
    sf.isRequired || sf.defaultPreset === preset || preset === 'enterprise'
  );
}

export default function PresetSelector({
  value,
  onChange,
  specFeatures,
}: Props) {
  const [hoveredPreset, setHoveredPreset] = useState<Preset | null>(null);

  return (
    <div className="relative" onMouseLeave={() => setHoveredPreset(null)}>
      <div className="grid grid-cols-3 gap-2">
        {PRESETS.map(({ id, label, description, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            onMouseEnter={() => setHoveredPreset(id)}
            className={cn(
              'flex flex-col items-center gap-1.5 p-3 rounded-lg border text-center transition-colors',
              value === id
                ? 'border-indigo-400 bg-indigo-50 text-indigo-900'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300',
            )}
          >
            <Icon
              className={cn(
                'h-5 w-5',
                value === id ? 'text-indigo-600' : 'text-gray-400',
              )}
            />
            <span className="text-xs font-semibold">{label}</span>
            <span className="text-xs text-gray-500 leading-tight">
              {description}
            </span>
          </button>
        ))}
      </div>

      {hoveredPreset && specFeatures && specFeatures.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3">
          <p className="text-xs font-semibold text-gray-700 mb-2 capitalize">
            {hoveredPreset} features
          </p>
          <div className="space-y-1">
            {specFeatures.map((sf) => {
              const enabled = isEnabledInPreset(sf, hoveredPreset);
              return (
                <div
                  key={sf.feature.slug}
                  className={`flex items-center gap-2 text-xs ${enabled ? 'text-emerald-700' : 'text-gray-400'}`}
                >
                  <span className="font-medium">{enabled ? '✓' : '–'}</span>
                  {sf.feature.name}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
